<template>
  <div class="flex bg-gray-50" style="height: calc(100vh - 120px);">
    <!-- Left Sidebar -->
    <div class="w-1/4 min-w-[300px] bg-white border-r border-gray-200 overflow-y-auto">
      <div class="p-4 border-b border-gray-200">
        <h2 class="text-lg font-semibold text-gray-800">EMAIL TEMPLATES</h2>
        <button @click="createNewTemplate"
          class="mt-2 w-full flex items-center justify-center p-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
          <span class="text-2xl text-gray-600">+</span>
        </button>
      </div>
      <div class="divide-y divide-gray-200">
        <div v-for="template in filteredTemplates" :key="template.id"
          class="p-3 hover:bg-gray-50 flex justify-between items-center"
          :class="{ 'bg-blue-50': currentTemplate.id === template.id }">
          <div @click="editTemplate(template)" class="cursor-pointer flex-grow">
            <h3 class="text-sm font-medium text-gray-900">{{ template.name }}</h3>
            <p class="text-sm text-gray-500 mt-1">{{ template.department }}</p>
          </div>
          <button @click.stop="deleteTemplateAction(template.id)" class="text-red-600 hover:text-red-800 focus:outline-none">
            <span class="sr-only">Delete</span>
            <span class="text-xl">&times;</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 overflow-y-auto">
      <div v-if="showEditor" class="max-w-4xl mx-auto p-6">
        <div class="space-y-4">
          <!-- Subject Field -->
          <div>
            <label class="block text-sm font-medium text-gray-700">Subject</label>
            <input type="text" v-model="currentTemplate.name"
              class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
            <p v-if="validationErrors.name" class="mt-1 text-sm text-red-600">{{ validationErrors.name }}</p>
          </div>

          <!-- From Field -->
          <div>
            <label class="block text-sm font-medium text-gray-700">From</label>
            <input type="email" v-model="currentTemplate.from"
              class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
            <p v-if="validationErrors.from" class="mt-1 text-sm text-red-600">{{ validationErrors.from }}</p>
          </div>

          <!-- To Field -->
          <div>
            <label class="block text-sm font-medium text-gray-700">To</label>
            <input type="text" v-model="toInput" @input="updateRecipients('to')"
              placeholder="Enter email addresses separated by commas"
              class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
          </div>

          <!-- CC Field -->
          <div>
            <label class="block text-sm font-medium text-gray-700">Cc</label>
            <input type="text" v-model="ccInput" @input="updateRecipients('cc')"
              placeholder="Enter email addresses separated by commas"
              class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
          </div>

          <!-- BCC Field -->
          <div>
            <label class="block text-sm font-medium text-gray-700">Bcc</label>
            <input type="text" v-model="bccInput" @input="updateRecipients('bcc')"
              placeholder="Enter email addresses separated by commas"
              class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
          </div>

          <!-- Department Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700">Department</label>
            <select v-model="currentTemplate.department"
              class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
              <option v-for="dept in availableDepartments" :key="dept" :value="dept">{{ dept }}</option>
            </select>
            <p v-if="validationErrors.department" class="mt-1 text-sm text-red-600">{{ validationErrors.department }}
            </p>
          </div>

          <!-- Survey Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700">Surveys</label>
            <select v-model="currentTemplate.surveyIds" multiple
              class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
              <option v-for="survey in filteredSurveys" :key="survey.id" :value="survey.id">{{ survey.title }}</option>
            </select>
            <p v-if="validationErrors.survey" class="mt-1 text-sm text-red-600">{{ validationErrors.survey }}</p>
          </div>

          <!-- Placeholder Section -->
          <div>
            <label class="block text-sm font-medium text-gray-700">Placeholders</label>
            <div class="mt-1 flex space-x-2">
              <select v-model="selectedPlaceholder" @change="insertPlaceholder"
                class="block w-1/2 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md">
                <option value="">Select a placeholder</option>
                <option v-for="placeholder in commonPlaceholders" :key="placeholder" :value="placeholder">
                  {{ placeholder }}
                </option>
              </select>
              <!--<input 
                v-model="customPlaceholder"
                @keyup.enter="insertCustomPlaceholder"
                type="text"
                placeholder="Custom placeholder"
                class="block w-1/2 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
              <button 
                @click="insertCustomPlaceholder"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Add
              </button>-->
            </div>
          </div>

          <div class="mb-4">
            <label for="templateType" class="block text-sm font-medium text-gray-700">Template Type</label>
            <select v-model="templateType" id="templateType"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
              <option value="Invitation">Invitation</option>
              <option value="Reminder">Reminder</option>
            </select>
          </div>

          <div v-if="isReminderType" class="mb-4">
            <label class="block text-sm font-medium text-gray-700">Trigger Days</label>
            <div class="mt-2 space-x-2">
              <label v-for="day in ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']" :key="day"
                class="inline-flex items-center">
                <input type="checkbox" v-model="triggerDays" :value="day"
                  class="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
                <span class="ml-2 text-sm text-gray-700">{{ day }}</span>
              </label>
            </div>
          </div>

          <!-- Content Editor -->
          <div>
            <label class="block text-sm font-medium text-gray-700">Content</label>
            <vue-editor v-model="currentTemplate.content" :editor-toolbar="customToolbar"
              class="border border-gray-300 rounded-md shadow-sm"></vue-editor>
          </div>

          <!-- Action Buttons -->
          <div class="flex justify-end space-x-3 pt-4">
            <button @click="trigger"
              class="px-4 py-2  bg-red-600 rounded-md text-sm font-medium text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              Send
            </button>
            <button @click="cancelEdit"
              class="ml-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              Cancel
            </button>
            <button @click="saveTemplate"
              class="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              Save
            </button>
          </div>
        </div>
      </div>
      <div v-else class="h-full flex items-center justify-center text-gray-500">
        Select a template from the sidebar or create a new one
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { VueEditor } from 'vue2-editor'
import { emailTemplateApi } from '@/api/emailTemplates';

export default {
  name: 'EmailTemplates',
  components: {
    VueEditor
  },
  data() {
    return {
      showEditor: false,
      isEditing: false,
      currentTemplate: {
        id: '',
        name: '',
        department: '',
        content: '',
        from: '',
        to: [],
        cc: [],
        bcc: []
      },
      toInput: '',
      ccInput: '',
      bccInput: '',
      customToolbar: [
        [{ 'header': [false, 1, 2, 3, 4, 5, 6] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'align': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['link', 'code-block'],
        ['clean']
      ],
      commonPlaceholders: [
        '{MODULE_NAME}',
        '{START_DATE}',
        '{END_DATE}',
        '{STUDENT_NAME}',
        '{INSTRUCTOR_NAME}',
        '{URL}'
      ],
      selectedPlaceholder: '',
      customPlaceholder: '',
      validationErrors: {
        name: '',
        from: '',
        department: ''
      },
      templateType: 'Invitation',
      triggerDays: [],
    }
  },
  computed: {
    ...mapState({
      surveys: state => state.surveys.surveys,
      templates: state => state.emailTemplates.templates,
      userRole: state => state.auth.userRole,
      userDepartment: state => state.auth.userDepartment,
      userSchool: state => state.auth.userSchool,
      schools: state => state.schools,
      departments: state => state.departments
    }),
    isReminderType() {
      return this.templateType === 'Reminder';
    },
    filteredSurveys() {
      return this.surveys.filter(x => x.department == this.currentTemplate.department)
    },
    filteredTemplates() {
      if (!this.schools || !this.departments) return [];

      if (this.userRole === 'admin') {
        return this.templates;
      } else if (this.userRole === 'school_admin') {
        const userSchool = this.schools.find(s => s.fullName === this.userSchool);
        if (!userSchool) return [];
        let departments = this.departments.filter(x => x.school == userSchool.shortName)
        console.log(departments)
        return this.templates.filter(t =>
          t.department === userSchool.fullName ||
          departments.findIndex(x => x.fullName == t.department) != -1
        );
      } else if (this.userRole === 'department_admin') {
      console.log(this.userDepartment)
        //const userDept = this.departments.find(d => d.shortName === this.userDepartment);
        //if (!userDept) return [];
        return this.templates.filter(t => t.department === this.userDepartment);
      }
      return [];
    },
    availableDepartments() {
      if (!this.schools || !this.departments) return [];
      if (this.userRole === 'admin') {
        this.schools.sort((a, b) => a.fullName.localeCompare(b.fullName));
        this.departments.sort((a, b) => a.fullName.localeCompare(b.fullName));
        return [...this.schools.map(d => d.fullName), ...this.departments.map(d => d.fullName)];
      } else if (this.userRole === 'school_admin') {
        const userSchool = this.schools.find(s => s.fullName === this.userSchool);
        const userDepartment = this.departments.filter(d => d.school === userSchool.shortName);
        userDepartment.sort((a, b) => a.fullName.localeCompare(b.fullName));

        //this.userDepartment.sort((a, b) => a.fullName.localeCompare(b.fullName));
        return [userSchool.fullName, ...userDepartment.map(d => d.fullName)];
      } else if (this.userRole === 'department_admin') {
        const userDept = this.departments.find(d => d.fullName === this.userDepartment);
        return userDept ? [userDept.fullName] : [];
      }
      return [];
    }
  },
  methods: {
    ...mapActions('emailTemplates', ['fetchTemplates', 'addTemplate', 'updateTemplate', 'deleteTemplate']),
    ...mapActions('surveys', ['fetchSurveys']),
    async trigger() {
      let yes = confirm('Are you sure you want to send this email?');
      if (!yes) return;
      await emailTemplateApi.trigger(this.currentTemplate.id);
    },
    createNewTemplate() {
      this.isEditing = false
      this.initializeResponses()
      this.showEditor = true
    },
    editTemplate(template) {
      this.isEditing = true
      this.currentTemplate = { ...template }
      this.toInput = template.to.join(', ')
      this.ccInput = template.cc.join(', ')
      this.bccInput = template.bcc.join(', ')
      this.templateType = template.templateType || 'Invitation';
      this.triggerDays = template.triggerDays || [];
      this.showEditor = true
    },
    initializeResponses() {
      this.currentTemplate = {
        id: '',
        name: '',
        department: '',
        content: '',
        from: '',
        to: [],
        cc: [],
        bcc: [],
        templateType: 'Invitation',
        triggerDays: []
      };
      this.toInput = '';
      this.ccInput = '';
      this.bccInput = '';
    },
    async saveTemplate() {
      // Reset validation errors
      this.validationErrors = {
        name: '',
        from: '',
        department: ''
      }

      // Validate required fields
      if (!this.currentTemplate.name.trim()) {
        this.validationErrors.name = 'Subject is required'
      }
      if (!this.currentTemplate.from.trim()) {
        this.validationErrors.from = 'From field is required'
      }
      if (!this.currentTemplate.department) {
        this.validationErrors.department = 'Department is required'
      }

      // Check if there are any validation errors
      if (Object.values(this.validationErrors).some(error => error)) {
        // If there are errors, don't proceed with saving
        return
      }

      try {
        const templateToSave = {
          ...this.currentTemplate,
          templateType: this.templateType,
          triggerDays: this.isReminderType ? this.triggerDays : []
        };

        if (this.isEditing) {
          await this.updateTemplate(templateToSave)
        } else {
          await this.addTemplate(templateToSave)
        }
        this.showEditor = false
        this.fetchTemplates()
      } catch (error) {
        console.error('Error saving template:', error)
        // Add error handling here
      }
    },
    cancelEdit() {
      this.showEditor = false
      this.initializeResponses()
    },
    async deleteTemplateAction(id) {
      if (confirm('Are you sure you want to delete this template?')) {
        try {
          await this.deleteTemplate(id)
          this.fetchTemplates()
          if (this.currentTemplate.id === id) {
            this.showEditor = false
            this.initializeResponses()
          }
        } catch (error) {
          console.error('Error deleting template:', error)
          // Add error handling here, e.g., show an error message to the user
        }
      }
    },
    updateRecipients(field) {
      const input = field + 'Input'
      this.currentTemplate[field] = this[input]
        .split(',')
        .map(email => email.trim())
        .filter(email => email !== '')
    },
    insertPlaceholder() {
      if (this.selectedPlaceholder) {
        this.currentTemplate.content += ` ${this.selectedPlaceholder}`;
        this.selectedPlaceholder = '';
      }
    },
    insertCustomPlaceholder() {
      if (this.customPlaceholder) {
        const formattedPlaceholder = `{${this.customPlaceholder.toUpperCase()}}`;
        this.currentTemplate.content += ` ${formattedPlaceholder}`;
        this.customPlaceholder = '';
      }
    }
  },
  mounted() {
    this.fetchTemplates()
    this.$store.dispatch('fetchSchools')
    this.$store.dispatch('fetchDepartments')
    this.fetchSurveys();

  }
}
</script>

<style>
.ql-editor {
  min-height: 300px;
  font-size: 14px;
}

.ql-toolbar {
  border-top-left-radius: 0.375rem;
  border-top-right-radius: 0.375rem;
  border-color: rgb(209, 213, 219);
}

.ql-container {
  border-bottom-left-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
  border-color: rgb(209, 213, 219);
}
</style>